import React from "react";
import {
  Button,
  Flex,
  Image,
  Input,
  Stack,
  Text,
  useToast,
  Box,
  InputGroup,
  InputRightElement,
  Badge,
  VStack,
  HStack,
  Tooltip,
  useColorModeValue,
  Spinner,
  Center,
  Select,
  useColorMode,
  CircularProgress,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import LandingFooter from "@/src/components/LandingFooter";
import LandingHeader from "@/src/components/LandingHeader";
import LandingShell from "@/src/layouts/LandingShell";
import useTranslation from "next-translate/useTranslation";
import TopBackground from "@/src/components/TopBackground";
import BlogSubscription from "@/src/components/BlogSubscription";
import useSWR from "swr";
import { useRouter } from "next/router";
import Pagination from "../components/Pagination";
import SecurityAlertBanner from "../components/search_cve/SecurityAlertBanner";
import SearchPagination from "../components/search_cve/SearchPagination";
import { FaSearch } from "react-icons/fa";

const SearchCveShell = ({ q,defaultPage }) => {
  return (
    <LandingShell>
      <LandingHeader />
      <TopBackground />
      <SearchContain q={q} defaultPage={defaultPage} />
      <LandingFooter />
    </LandingShell>
  );
};

// Composant pour un élément CVE
const CveListItem = ({ cve, stripped = false, isEven = false }) => {
  // Couleurs pour le mode stripped
  const strippedBgLight = isEven ? "brand.10" : "white";
  const strippedBgDark = isEven ? "brand.600" : "brand.500";
  const router = useRouter();

  // Couleur de fond en fonction du mode
  const bgColor = stripped
    ? useColorModeValue(strippedBgLight, strippedBgDark)
    : useColorModeValue("white", "gray.700");

  const handleSearchCpe = (cpeValue) => {
    router.push(`/search-cve?q=${encodeURIComponent(cpeValue)}`);
  };

  // Style du conteneur en fonction du mode
  const containerProps = stripped
    ? {
        p: 3,
        borderBottomWidth: "1px",
        borderBottomColor: useColorModeValue("gray.100", "gray.600"),
        boxShadow: "none",
        borderRadius: 0,
        _hover: { bg: useColorModeValue("brand.10", "brand.600") },
      }
    : {
        p: 4,
        mb: 4,
        borderRadius: "md",
        boxShadow: "sm",
        _hover: { boxShadow: "md" },
      };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return "gray";
    if (score >= 7) return "red";
    if (score >= 4) return "orange";
    return "green";
  };

  const displayScore =
    cve.cvss?.baseScore !== null && cve.cvss?.baseScore !== undefined
      ? cve.cvss.baseScore.toFixed(1)
      : "N/A";

  const scoreColor = getScoreColor(cve.cvss?.baseScore);
  const cvssVersion = cve.cvss?.version ? `CVSSv${cve.cvss.version}` : "CVSS";
  const truncatedDescription = cve.description
    ? `${cve.description.split(" ").slice(0, 20).join(" ")}...`
    : "No description available";

  const products = cve.cpes?.map((cpe) => cpe.name ?? "") || [];

  return (
    <Box
      bg={bgColor}
      transition="background 0.2s"
      cursor={"pointer"}
      onClick={() => {
        router.push(`/vulnerability-detail?cve_id=${cve.cve_id}`);
      }}
      {...containerProps}
    >
      <Flex align="center">
        <VStack align="center" w="80px" mr={4}>
          <Badge
            fontSize="2xl"
            colorScheme={scoreColor}
            px={4}
            py={2}
            borderRadius="full"
          >
            {displayScore}
          </Badge>
          <Text fontSize="xs" color={useColorModeValue("gray.500", "white")}>
            {cvssVersion}
          </Text>
        </VStack>

        <VStack align="flex-start" spacing={1} flex={1}>
          <Text fontWeight="bold" color={useColorModeValue("inherit", "white")} fontSize="lg">
            {cve.cve_id}
          </Text>

          <Text
            noOfLines={3}
            fontSize="sm"
            color={useColorModeValue("gray.600", "gray.300")}
          >
            {truncatedDescription}
          </Text>

          {products.length > 0 && (
            <HStack gap={2} mt={1} flexWrap="wrap">
              {products.slice(0, 4).map((product, index) => (
                <Badge
                  key={index}
                  colorScheme="brand"
                  variant="subtle"
                  fontSize="xs"
                  mx={0}
                  marginStart={0}
                  marginEnd={0}
                  style={{ marginInline: 0 }}
                  onClick={() => {
                    handleSearchCpe(product);
                  }}
                  cursor="pointer"
                  _hover={{ bg: "brand.100", color: "brand.500" }}
                >
                  {product}
                </Badge>
              ))}
              ...
            </HStack>
          )}
        </VStack>
      </Flex>
    </Box>
  );
};
// Composant pour la liste paginée
const CveList = ({ searchQuery,defaultPage=1 }) => {
  const toast = useToast();
  const router = useRouter();
  const {t}=useTranslation();
  const { colorMode } = useColorMode();

  const [pageIndex, setPageIndex] = React.useState(defaultPage);
  const [searchPageIndex, setSearchPageIndex] = React.useState(pageIndex);

  const [pageSize, setPageSize] = React.useState(10);
  console.log("dep:", defaultPage);
  const fetcher = (url) =>
    fetch(url).then((res) => {
      if (!res.ok) throw new Error("Failed to fetch");
      console.log(res);
      return res.json();
    });

  const { data, error, isValidating } = useSWR(
    `/api/search?q=${encodeURIComponent(
      searchQuery
    )}&page=${pageIndex}&limit=${pageSize}`,
    fetcher,
    {
      revalidateOnFocus: false,
      onError: (err) => {
        toast({
          title: "Error",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    }
  );

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    setSearchPageIndex(newPage);
    router.replace(`/search-cve?q=${encodeURIComponent(searchQuery)}&page=${newPage}`);
    window.scrollTo({ top: 100, behavior: "smooth" });
  };

  const searchResults = data?.cves?.results || data?.cves || [];
  console.log("Search Results:", searchResults);
  const totalCount = data?.cves.count || 0;

  const isLoading = isValidating && !data;

  //   pagination logic
  const hasNext = data?.cves?.page < data?.cves?.total_pages ?? false;
  const hasPrevious = data?.cves?.page>1 ?? false;

  function goNext() {
    if (hasNext) {
      handlePageChange(pageIndex + 1);
      //   nextPage();
    }
  }
  function goPrevious() {
    if (hasPrevious) {
      handlePageChange(pageIndex - 1);
    }
  }

  const onSearchIndexChange = (e) => {
    e?.preventDefault();
    setSearchPageIndex(e?.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (
        !e?.target.value ||
        e?.target.value < 1 ||
        e?.target.value > Math.ceil(totalCount / 10)
      ) {
        setSearchPageIndex(1);
      }
      handlePageChange(Number(searchPageIndex));
    }
  };
  // end

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {/* Liste des CVE */}
      <Box>
        {isLoading ? (
          <Center py={10}>
            <CircularProgress
                      capIsRound={true}
                      thickness={12}
                      trackColor={"brand.50"}
                      size={"60px"}
                      color={"brand.500"}
                      isIndeterminate
                    />
          </Center>
        ) : error ? (
          <Text color="red.500">{t("dashboard:search_cve.no_results")}</Text>
        ) : searchResults.length > 0 ? (
          <VStack spacing={4} align="stretch">
            {searchResults.map((cve, index) => (
              <CveListItem
                key={cve.id}
                cve={cve.source_json??cve}
                stripped={true}
                isEven={index % 2 == 0}
              />
            ))}
          </VStack>
        ) : (
          <Text textAlign="center" py={10}>
            {t("dashboard:search_cve.no_results")}
          </Text>
        )}
      </Box>

      {totalCount > 0 && (
        <SearchPagination
          colorMode={colorMode}
          objectCount={totalCount}
          hasPrevious={hasPrevious}
          hasNext={hasNext}
          goPrevious={goPrevious}
          goNext={goNext}
          goToPage={handlePageChange}
          searchPageIndex={searchPageIndex}
          onSearchIndexChange={onSearchIndexChange}
          handleKeyDown={handleKeyDown}
          pageCount={pageIndex}
        />
      )}
    </VStack>
  );
};

const SearchContain = ({ q,defaultPage }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const router = useRouter();
  const [inputValue, setInputValue] = React.useState(q || "");
  const [searchQuery, setSearchQuery] = React.useState(q || "");

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(inputValue);
    router.replace(`/search-cve?q=${encodeURIComponent(inputValue)}`);
  };

  return (
    <Stack
      py={"0px"}
      w={{ base: "full", "2xl": "full" }}
      direction={"column"}
      alignItems={"center"}
      mb={"60px"}
      mt="0"
      position="relative"
      top="-30px"
    >
      <Box
  as={Flex}
  direction="column"
  justifyContent={"center"}
  alignItems="center"
  zIndex={9}
  minH={searchQuery== "" ? "calc(100vh - 60px)" : "340px"}
  w="full"
  bgColor={"#00B295"}
  position="relative"
>
  <Image
    alt={"dot-top"}
    src={"/images/link_verification_vector.svg"}
    right={"0px"}
    position={"absolute"}
    top={"-30px"}
  />
  <Image
    alt={"dot-top"}
    src={"/images/link_vector3.svg"}
    left={"0px"}
    position={"absolute"}
    bottom={"-40px"}
  />
  <Image
    alt={"dot-top"}
    src={"/images/link_vector2.png"}
    left={"45px"}
    position={"absolute"}
    bottom={"-40px"}
  />
  <Text
    color={"white"}
    fontWeight={500}
    fontSize={"24px"}
    textAlign={"center"}
  >
    {t("dashboard:search_cve.title")}
  </Text>
  <Text
    color={"white"}
    fontWeight={400}
    fontSize={"16px"}
    textAlign={"center"}
    marginBottom="57px"
  >
    {t("dashboard:search_cve.description")}
  </Text>

  {/* Champ de recherche */}
 <Box
    maxWidth={{ base: "90%", lg: "724px" }}
    height={"50px"}
    minWidth={{ base: "90%", lg: "724px" }}
    px={{ base: "20px", lg: "0" }}
  >
    <form onSubmit={handleSearch}>
      <InputGroup mt={5} transform={"auto"}>
        <Input
          borderRadius={"4"}
          flex={"1"}
          placeholder={t("dashboard:search_cve.search_placeholder")}
          id="search"
          border={"0"}
          name={"search"}
          value={inputValue}
          onChange={handleInputChange}
          backgroundColor="#00000036"
          color="#fff"
          _hover={{ backgroundColor: "#00000036" }}
          _focus={{ backgroundColor: "#00000036" }}
          pr={{ base: "60px", md: "130px" }} // Ajouter du padding à droite pour éviter le chevauchement
        />

        <InputRightElement width={{ base: "50px", md: "130px" }} h="full" pr={{ base: "0", md: "4px" }}>
          {/* Button pour desktop */}
          <Button
            borderRadius={"4px"}
            color="brand.500"
            h="38px" // Légèrement plus petit que le input
            fontSize="16px"
            fontWeight="500"
            type={"submit"}
            backgroundColor="white"
            px="20px"
            display={{ base: "none", md: "flex" }}
            _hover={{ backgroundColor: "gray.50" }}
          >
            {t("dashboard:search_cve.search_button")}
          </Button>
          
          {/* IconButton pour mobile */}
          <IconButton
            borderRadius={"4px"}
            color="brand.500"
            h="38px"
            fontSize="16px"
            fontWeight="500"
            type={"submit"}
            backgroundColor="white"
            icon={<Icon as={FaSearch} color={"brand.500"} />}
            display={{ base: "flex", md: "none" }}
            _hover={{ backgroundColor: "gray.50" }}
            aria-label="Search"
          />
        </InputRightElement>
      </InputGroup>
    </form>
  </Box>
</Box>
{searchQuery!== "" && (
      <Flex
        gap="70px"
        w="full"
        direction={{ base: "column", md: "row" }}
        position={"relative"}
        px={{base:"30px", "2xl": "120px", "5xl": "450px" }}
      >
        <Stack flex="1">
          <Text
            color={useColorModeValue("gray.500", "white")}
            fontWeight={500}
            fontSize={"25px"}
            textAlign={"left"}
            my="30px"
          >
            {t("dashboard:search_cve.search_results")}
          </Text>
          <CveList searchQuery={searchQuery} defaultPage={defaultPage} />
        </Stack>

        {/* Correction du sticky */}
        <Stack
          maxW="368px"
          position="sticky"
          top="100px"
          alignSelf="flex-start"
          marginTop="30px"
          height="fit-content"
          zIndex={1}
          gap={2}
        >
          <SecurityAlertBanner w="full" />
          <BlogSubscription w="full" />
        </Stack>
      </Flex>
      )}
    </Stack>
  );
};

export default SearchCveShell;
